import {styled} from '@linaria/react';
import React, {ComponentProps, FC, ReactNode} from 'react';
import {
  BannerMedia,
  BannerMediaData,
} from '../../../molecules/PromotionBanner/BannerMedia';
import {EllipseGlow, buildSpotlightGlowsCss} from '../glows';

export interface SpotlightSlideBackgroundProps {
  /** Solid base color (hex) rendered behind the glow blobs. */
  backgroundColor: string;
  /**
   * FE-owned glow positions. The caller picks the breakpoint set
   * (`SPOTLIGHT_GLOWS_MOBILE` vs `SPOTLIGHT_GLOWS_DESKTOP`); the primitive
   * itself is breakpoint-agnostic and resolution-independent.
   */
  glows: EllipseGlow[];
  /**
   * `[inner, outer?]` stop pair of the shared glow gradient as ready-to-use
   * CSS color strings (e.g. `rgba(...)`), applied to every glow slot.
   * Empty/undefined ⇒ no glow layer, just the solid base color.
   */
  glowColors?: string[];
  /**
   * Optional hero image/video. Its presence — and only its presence — adds the
   * legibility scrim over the media; glow-only slides never get a scrim.
   */
  media?: BannerMediaData;
  /** Pass `eager` only when the media is visible on page load. See `Video`. */
  mediaLoading?: ComponentProps<typeof BannerMedia>['loading'];
  /** Foreground content rendered above the whole background stack. */
  children?: ReactNode;
  className?: string;
  ['data-testid']?: string;
}

/**
 * Presentational background stack for the Spotlight slides. Fills its
 * positioned parent (`position: absolute; inset: 0`) with no fixed height, so
 * the same primitive works for the tall mobile card and the wide desktop
 * split-panel — the consuming shell owns the box size and click semantics.
 *
 * Layers, bottom to top (source order = stacking order, mirroring the banner's
 * `BgLayer` / `GlowLayer` / `MediaLayer`):
 *   1. solid base color;
 *   2. glow blobs (only when `glowColors` is non-empty);
 *   3. media image/video (only when `media` is present);
 *   4. media-only scrim (present iff `media` is present);
 *   5. foreground `children`.
 */
export const SpotlightSlideBackground: FC<SpotlightSlideBackgroundProps> = ({
  backgroundColor,
  glows,
  glowColors,
  media,
  mediaLoading,
  children,
  className,
  'data-testid': dataTestid,
}) => {
  const hasGlow = Boolean(glowColors && glowColors.length > 0);
  const hasMedia = Boolean(media);

  return (
    <Root className={className} data-testid={dataTestid}>
      <BaseLayer
        style={{backgroundColor}}
        data-testid="spotlight-background-base"
      />

      {hasGlow && (
        <GlowLayer
          style={{backgroundImage: buildSpotlightGlowsCss(glows, glowColors)}}
          data-testid="spotlight-background-glow"
        />
      )}

      {hasMedia && (
        <>
          <MediaLayer>
            <BannerMedia
              media={media}
              loading={mediaLoading}
              data-testid="spotlight-background-media"
            />
          </MediaLayer>
          <ScrimLayer data-testid="spotlight-background-scrim" />
        </>
      )}

      {children != null && (
        <ForegroundLayer data-testid="spotlight-background-foreground">
          {children}
        </ForegroundLayer>
      )}
    </Root>
  );
};

const Root = styled.div`
  position: absolute;
  inset: 0;
  overflow: hidden;
`;

const BaseLayer = styled.div`
  position: absolute;
  inset: 0;
`;

const GlowLayer = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
`;

const MediaLayer = styled.div`
  position: absolute;
  inset: 0;
`;

const ScrimLayer = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0) 0.08%,
    rgba(0, 0, 0, 0.04) 25.61%,
    rgba(0, 0, 0, 0.15) 41.99%,
    rgba(0, 0, 0, 0.4) 96.1%
  );
`;

const ForegroundLayer = styled.div`
  position: absolute;
  inset: 0;
`;
